import React, { useState } from 'react';
import { Navbar, Nav, NavDropdown, Container, Form, Image, Dropdown, Badge, Button, Offcanvas } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaSearch, FaBell, FaCog, FaExpand, FaCompress, FaSignOutAlt, FaUser, FaEnvelope, FaIdBadge, FaBarcode, FaDatabase, FaServer } from 'react-icons/fa';
import { logout } from '../../../../redux/features/auth.features';
import { useFullscreen } from '../../../core/hooks/useFullscreen';
import { useSession } from '../../../core/context/SessionContext';
import { menuData } from '../../../../config/menuConfig';

const TopMenuBar = ({ navigationItems }) => {
    const { user } = useSelector(state => state.authReducer);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { toggleFullScreen, isFullscreen } = useFullscreen();
    const { timeLeft, formatEasyTime, getActiveDuration } = useSession();
    const [expanded, setExpanded] = useState(false);

    // Permission lookup logic
    const permissionLookup = (navigationItems || []).reduce((acc, item) => {
        acc[item.path.split("/")[1] || item.path] = true;
        return acc;
    }, {});

    // Filter menu groups and items based on sections and permissions
    const filteredMenu = menuData.map(group => {
        // Privacy Guard: Hide Accounts and Intelligence from non-privileged roles
        const isPrivileged = user?.roles?.some(r => ['Admin', 'Manager'].includes(r.name));
        const isSensitiveGroup = ['Accounts', 'Intelligence'].includes(group.title);

        if (isSensitiveGroup && !isPrivileged) return null;

        const sections = (group.sections || []).map(section => ({
            ...section,
            items: section.items.filter(
                (item) => permissionLookup[item.path] || permissionLookup[item.path.split('/')[0]]
            )
        })).filter(section => section.items.length > 0);
        return { ...group, sections };
    }).filter(group => group !== null && group.sections.length > 0);

    const handleLogOut = () => {
        dispatch(logout()).then(() => {
            navigate('/login');
        });
    };

    const employeeData = user?.employeeId;
    const displayName = employeeData?.name || user?.username || 'User';
    const displayEmail = employeeData?.email || user?.email;
    const displayRoleName = user?.roles?.[0]?.name || 'Staff';

    // Responsive Topbar Styles
    const topBarStyles = {
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(231, 234, 243, 0.7)',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
        padding: '0.5rem 0', // Adjusted padding
        position: 'sticky',
        top: 0,
        width: '100%',
        zIndex: 1020,
    };

    //console.log("filteredMenu ", filteredMenu)

    return (
        <Navbar expand="lg" style={topBarStyles} expanded={expanded} onToggle={setExpanded} className="w-100 p-0">
            <Container fluid className="px-3 px-lg-4">
                {/* Brand Logo */}
                <Navbar.Brand as={Link} to="/" className="d-flex align-items-center me-3 text-decoration-none" style={{ minWidth: 'fit-content' }}>
                    <div className="d-flex align-items-center gap-2">
                        <div 
                            className="rounded-3 d-flex align-items-center justify-content-center text-white shadow-sm flex-shrink-0"
                            style={{ 
                                width: '34px', 
                                height: '34px', 
                                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                                fontSize: '1.1rem' 
                            }}
                        >
                            <i className="bx bx-mobile-alt"></i>
                        </div>
                        <div className="d-flex flex-column text-start">
                            <div className="fw-bold text-dark d-flex align-items-center gap-1" style={{ fontSize: '0.92rem', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                                iShopMaster <span className="badge bg-primary bg-opacity-10 text-primary py-0 px-1" style={{ fontSize: '0.6rem', fontWeight: 800 }}>PRO</span>
                            </div>
                            <span className="text-muted fw-semibold" style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}>REPAIR MANAGEMENT</span>
                        </div>
                    </div>
                </Navbar.Brand>

                {/* Mobile Toggler */}
                <Navbar.Toggle aria-controls="top-navbar-nav" className="border-0 shadow-none p-0">
                    <span className="navbar-toggler-icon" style={{ width: '1.2em', height: '1.2em' }}></span>
                </Navbar.Toggle>

                {/* Collapsible Content */}
                <Navbar.Collapse id="top-navbar-nav">
                    {/* Main Menu Items */}
                    <Nav className="me-auto gap-1 my-2 my-lg-0">
                        {filteredMenu.map((group) => {
                            const isInventory = group.title === 'Inventory';

                            return (
                                <NavDropdown
                                    key={group.title}
                                    title={
                                        <span className="d-flex align-items-center" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                            <i className={`${group.icon} me-2 menu-icon`}></i>
                                            {group.title}
                                        </span>
                                    }
                                    id={`${group.title.toLowerCase()}-dropdown`}
                                    className={`premium-dropdown ${isInventory ? 'mega-menu-trigger' : ''}`}
                                >
                                    <div className={isInventory ? "mega-menu-container p-3" : ""}>
                                        <div className={isInventory ? "row g-3" : ""}>
                                            {group.sections.map((section, sIdx) => (
                                                <div key={section.title} className={isInventory ? "col-lg-4 col-md-12" : ""}>
                                                    {!isInventory && sIdx > 0 && <div className="dropdown-divider mx-2"></div>}

                                                    <div className="dropdown-grid-header px-3 py-2 text-uppercase text-primary fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.5px', borderBottom: isInventory ? '1px solid rgba(105, 108, 255, 0.1)' : 'none', marginBottom: isInventory ? '8px' : '0' }}>
                                                        {section.title}
                                                    </div>

                                                    {section.items.map((item) => (
                                                        <NavDropdown.Item
                                                            key={item.title}
                                                            as={Link}
                                                            to={item.path}
                                                            className="d-flex align-items-center py-2 px-3 rounded-2 my-1 dropdown-item-custom"
                                                            onClick={() => setExpanded(false)}
                                                        >
                                                            <div className="dropdown-icon-wrapper me-3 bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px', minWidth: '28px' }}>
                                                                <i className={item.icon} style={{ fontSize: '0.9rem', color: '#696cff' }}></i>
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold text-dark dropdown-item-title" style={{ fontSize: '0.8rem' }}>{item.title}</div>
                                                            </div>
                                                        </NavDropdown.Item>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </NavDropdown>
                            );
                        })}
                    </Nav>

                    {/* Right Side Tools */}
                    <Nav className="align-items-center gap-2 flex-row justify-content-end mt-3 mt-lg-0">
                        {/* Search - Hidden on mobile, visible lg+ */}
                        <div className="topbar-search d-none d-lg-block me-2">
                            <Form.Control
                                type="text"
                                placeholder="Search (Ctrl+K)"
                                className="bg-light border-0 px-3 rounded-pill shadow-none"
                                style={{ width: '180px', fontSize: '0.8rem' }}
                            />
                        </div>

                        {/* Shortcuts */}
                        <Button variant="link" className="nav-link text-secondary p-2 d-none d-md-block" onClick={toggleFullScreen} title="Fullscreen">
                            {isFullscreen ? <FaCompress size={16} /> : <FaExpand size={16} />}
                        </Button>


                        {/* Notifications */}
                        <div className="nav-item mx-1 position-relative cursor-pointer">
                            <FaBell size={18} className="text-secondary" />
                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style={{ fontSize: '0.5rem', padding: '2px 4px' }}>
                                3
                            </span>
                        </div>

                        {/* Session Timer */}
                        <div className="d-none d-lg-flex flex-column align-items-end bg-light px-3 py-1 rounded border me-2" style={{ fontSize: '0.7rem', minWidth: '140px' }}>
                            <div className="d-flex align-items-center mb-1">
                                <div className="pulse-dot me-2"></div>
                                <span className="text-muted fw-bold me-1">ACTIVE FOR:</span>
                                <span className="text-success fw-bold font-monospace">{formatEasyTime(getActiveDuration())}</span>
                            </div>
                            <div style={{ fontSize: '0.6rem' }} className="text-muted text-uppercase">
                                Auto-logout in: <span className="text-primary fw-bold">{formatEasyTime(timeLeft)}</span>
                            </div>
                        </div>

                        {/* User Profile */}
                        <Dropdown align="end">
                            <Dropdown.Toggle as="div" className="d-flex align-items-center ps-2 cursor-pointer border-start ms-2" id="user-top-dropdown">
                                <div className="text-end me-2 d-none d-md-block">
                                    <div className="fw-bold text-dark mb-0 line-height-1" style={{ fontSize: '0.8rem' }}>{user?.username || 'User'}</div>
                                    <div className="text-muted text-uppercase" style={{ fontSize: '0.6rem' }}>{displayRoleName}</div>
                                </div>
                                <div className="avatar avatar-sm avatar-online shadow-sm">
                                    <Image src="assets/img/avatars/1.png" roundedCircle height={32} width={32} style={{ objectFit: 'cover' }} />
                                </div>
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="shadow-lg border-0 py-3 px-2 dropdown-menu-end mt-2" style={{ minWidth: '260px', borderRadius: '12px' }}>
                                <div className="px-3 pb-3 border-bottom mb-2 d-flex align-items-center">
                                    <div className="avatar avatar-md me-3">
                                        <Image src="assets/img/avatars/1.png" roundedCircle height={40} width={40} />
                                    </div>
                                    <div>
                                        <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{displayName}</h6>
                                        <Badge bg="label-primary" className="text-primary bg-primary bg-opacity-10 py-1" style={{ fontSize: '0.65rem' }}>{displayRoleName}</Badge>
                                    </div>
                                </div>

                                <Dropdown.Item as={Link} to="/profile" className="d-flex align-items-center py-2 rounded-2" style={{ fontSize: '0.85rem' }}>
                                    <FaUser className="me-2 text-primary" size={14} /> My Profile
                                </Dropdown.Item>
                                <Dropdown.Item as={Link} to="/CompanyProfile" className="d-flex align-items-center py-2 rounded-2" style={{ fontSize: '0.85rem' }}>
                                    <FaCog className="me-2 text-secondary" size={14} /> Company Settings
                                </Dropdown.Item>
                                {user?.roles?.some(r => r.name === 'SuperAdmin') && (
                                    <>
                                        <Dropdown.Divider />
                                        <div className="px-3 py-1 text-uppercase fw-bold text-danger" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                                            Provider Hub (Hub Registry)
                                        </div>
                                        <Dropdown.Item as={Link} to="/admin/tenants" className="d-flex align-items-center py-2 rounded-2" style={{ fontSize: '0.85rem' }}>
                                            <FaDatabase className="me-2 text-danger" size={14} /> Tenant Registry (Nodes)
                                        </Dropdown.Item>
                                        <Dropdown.Item as={Link} to="/admin/quota" className="d-flex align-items-center py-2 rounded-2" style={{ fontSize: '0.85rem' }}>
                                            <FaServer className="me-2 text-dark" size={14} /> Global Quota Hub
                                        </Dropdown.Item>
                                    </>
                                )}

                                <Dropdown.Divider />

                                <div className="px-3 py-2 text-muted d-flex flex-column gap-1" style={{ fontSize: '0.75rem' }}>
                                    <div className="d-flex align-items-center text-truncate"><FaEnvelope className="me-2" size={12} /> {displayEmail}</div>
                                    <div className="d-flex align-items-center"><FaIdBadge className="me-2" size={12} /> ID: {user?._id?.substring(0, 8)}...</div>
                                </div>

                                <Dropdown.Divider />

                                <Dropdown.Item onClick={handleLogOut} className="d-flex align-items-center text-danger py-2 rounded-2 fw-bold" style={{ fontSize: '0.85rem' }}>
                                    <FaSignOutAlt className="me-2" size={14} /> Sign Out
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Nav>
                </Navbar.Collapse>
            </Container>


            <style>
                {`
                    /* General Navbar Tweaks */
                    .navbar-toggler {
                        padding: 0.25rem 0.5rem;
                    }
                    
                    /* Dropdown Styling */
                    .premium-dropdown .dropdown-toggle {
                        background: transparent !important;
                        border: 0 !important;
                        color: #566a7f !important;
                        padding: 0.5rem 0.8rem !important;
                        border-radius: 6px !important;
                        transition: all 0.2s ease;
                    }
                    
                    .premium-dropdown .dropdown-toggle::after {
                        margin-left: 0.4em;
                        vertical-align: 0.15em;
                        border: solid #b4bdc6;
                        border-width: 0 1.5px 1.5px 0;
                        display: inline-block;
                        padding: 2px;
                        transform: rotate(45deg);
                        transition: transform 0.2s;
                    }

                    .premium-dropdown.show .dropdown-toggle::after {
                        transform: rotate(-135deg);
                    }

                    .premium-dropdown .dropdown-menu {
                        border: 0;
                        box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                        border-radius: 12px;
                        padding: 0.5rem;
                        margin-top: 8px;
                        animation: slideUp 0.2s ease-out;
                    }

                    /* Mega Menu Logic */
                    /* Desktop: Fixed width centered */
                    @media (min-width: 992px) {
                        .mega-menu-trigger .dropdown-menu {
                            width: 600px;
                            left: 50% !important;
                            transform: translateX(-50%) !important;
                        }
                    }

                    /* Mobile/Tablet: Full width */
                    @media (max-width: 991px) {
                        .mega-menu-trigger .dropdown-menu {
                            width: 100%;
                            position: static;
                            float: none;
                            box-shadow: none;
                            border: 1px solid #eee;
                            background: #fdfdfd;
                            margin-top: 0;
                        }
                        
                        .navbar-nav .dropdown-menu {
                            border: none;
                            background-color: transparent;
                            box-shadow: none;
                            padding-left: 1rem;
                        }
                    }

                    /* Dropdown Items */
                    .dropdown-item-custom:hover {
                        background-color: rgba(105, 108, 255, 0.08);
                        color: #696cff;
                    }
                    
                    .dropdown-item-custom:hover .dropdown-icon-wrapper {
                        background-color: #696cff !important;
                    }
                    
                    .dropdown-item-custom:hover .dropdown-icon-wrapper i {
                        color: #ffffff !important;
                    }

                    @keyframes slideUp {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .pulse-dot {
                        width: 8px;
                        height: 8px;
                        background: #10b981;
                        border-radius: 50%;
                        box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
                        animation: pulse-green 2s infinite;
                    }

                    @keyframes pulse-green {
                        0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
                        70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
                        100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
                    }
                `}
            </style>
        </Navbar>
    );
};

export default TopMenuBar;